import { useState } from 'react';
import { api, ApiError, type SavedCopyKind, type SavedCopyView } from '../api/client';
import { fullWhen, plural, sizeOf } from '../lib/format';
import { Icon } from '../lib/icons';
import { useData } from '../lib/useData';
import { Button, Card, Dialog, LoadError, Loading, Notice } from './ui';

const KIND_WORD: Record<SavedCopyKind, string> = { ringGroup: 'ring group', callQueue: 'call queue', autoAttendant: 'auto attendant' };

/**
 * Saved copies (1.13.0, Teampace 2718) at the foot of Ring groups, Call queues and Auto attendants: a
 * copy of one, saved on its own or as it was removed, that can be rebuilt later. Rebuilding fills the
 * add form with its settings; nothing changes on the phone system until the addition is reviewed and
 * applied like any other.
 */
export function SavedCopiesCard({
  domain,
  kind,
  writable,
  refresh,
  onRebuild
}: {
  domain: string;
  kind: SavedCopyKind;
  writable: boolean;
  /** Changes when a copy was just saved elsewhere on the page. */
  refresh: number;
  onRebuild: (r: { extension: string; free: boolean; values: Record<string, string>; left: string[]; name: string }) => void;
}) {
  const data = useData(() => api.savedCopies(domain, kind), [domain, kind, refresh]);
  const [busy, setBusy] = useState('');
  const [note, setNote] = useState<{ tone: 'ok' | 'crit'; text: string } | null>(null);
  const [removing, setRemoving] = useState<SavedCopyView | null>(null);
  const word = KIND_WORD[kind];

  const rebuild = async (c: SavedCopyView) => {
    setBusy(c.id);
    setNote(null);
    try {
      onRebuild(await api.rebuildCopy(domain, c.id));
    } catch (err) {
      setNote({ tone: 'crit', text: err instanceof ApiError ? err.message : 'The copy couldn’t be opened. Try again.' });
    } finally {
      setBusy('');
    }
  };

  const remove = async (c: SavedCopyView) => {
    setBusy(c.id);
    setNote(null);
    try {
      await api.removeCopy(domain, c.id);
      setRemoving(null);
      setNote({ tone: 'ok', text: `Removed the saved copy of ${c.name}.` });
      data.reload();
    } catch (err) {
      setNote({ tone: 'crit', text: err instanceof ApiError ? err.message : 'The copy couldn’t be removed. Try again.' });
    } finally {
      setBusy('');
    }
  };

  const copies = data.data?.copies ?? [];
  return (
    <Card title="Saved copies" action={data.data ? <span className="muted small">{plural(copies.length, 'copy', 'copies')}</span> : undefined}>
      <div className="card-body">
        {data.error ? (
          <LoadError error={data.error} retry={data.reload} />
        ) : !data.data ? (
          <Loading label="Loading saved copies" />
        ) : (
          <>
            <p className="muted small">
              A copy keeps every setting of a {word}, so it can be rebuilt after it’s removed. Save one from a {word}’s row, or when you remove it. Copies stay until you remove them
              {data.data.where === 'archive' ? ', in your company’s archive.' : '.'}
            </p>
            {note && (
              <Notice tone={note.tone} title={note.tone === 'ok' ? 'Done' : 'That didn’t work'}>
                {note.text}
              </Notice>
            )}
            {copies.length === 0 ? (
              <p className="muted">No saved copies yet.</p>
            ) : (
              <ul className="saved-copies">
                {copies.map((c) => (
                  <li key={c.id}>
                    <div className="grow">
                      <strong>{c.name}</strong> <span className="muted">({c.extension})</span>
                      <div className="muted small">
                        {c.reason === 'before-removal' ? 'Saved as it was removed' : 'Saved'} {fullWhen(c.savedAt)}
                        {c.savedByName ? ` by ${c.savedByName}` : ''} · {sizeOf(c.bytes)}
                      </div>
                    </div>
                    <div className="row-actions">
                      <Button size="small" variant="secondary" icon="undo" disabled={!writable || Boolean(busy)} onClick={() => void rebuild(c)}>
                        Rebuild
                      </Button>
                      <a className="icon-btn" href={api.savedCopyFileUrl(domain, c.id)} download aria-label={`Download the saved copy of ${c.name}`} title="Download (JSON)">
                        <Icon name="download" size={16} />
                      </a>
                      <button type="button" className="icon-btn" aria-label={`Remove the saved copy of ${c.name}`} title="Remove the copy" disabled={Boolean(busy)} onClick={() => setRemoving(c)}>
                        <Icon name="trash" size={16} />
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </>
        )}
      </div>
      {removing && (
        <Dialog
          open
          onClose={() => setRemoving(null)}
          title={`Remove the saved copy of ${removing.name}?`}
          subtitle={`Saved ${fullWhen(removing.savedAt)}`}
          foot={
            <>
              <Button variant="ghost" onClick={() => setRemoving(null)}>
                Keep it
              </Button>
              <Button variant="danger" icon="trash" disabled={Boolean(busy)} onClick={() => void remove(removing)}>
                Remove the copy
              </Button>
            </>
          }
        >
          <p>The {word} can’t be rebuilt from this copy afterwards. Download it first if you might want it.</p>
        </Dialog>
      )}
    </Card>
  );
}
